import { Helmet } from "react-helmet-async";

/**
 * <SEO> — one component per page. Handles <title>, meta description,
 * Open Graph, Twitter cards, canonical, and noindex.
 *
 * Defaults are Viyora-branded so any page that skips props still gets a good SERP entry.
 */
const DEFAULT_TITLE = "Viyora – AI-powered Clinic Management Software";
const DEFAULT_DESC =
  "Viyora is an AI-powered clinic management platform for dentists and healthcare practices. Manage appointments, patient records, GST billing, inventory, WhatsApp reminders and analytics from one secure platform.";
const DEFAULT_OG_IMAGE = "/og-cover.png"; // 1200x630 in /public
const SITE_URL = "https://viyora.co.in";

export default function SEO({
  title,
  description = DEFAULT_DESC,
  path = "",           // e.g. "/pricing" → canonical is https://viyora.co.in/pricing
  ogImage = DEFAULT_OG_IMAGE,
  noindex = false,
  keywords,            // optional comma-separated string
  structuredData,      // optional JSON-LD object
}) {
  // Suppress the " · Viyora" suffix when the title already ends with the
  // brand name — avoids "Viyora – AI-powered … · Viyora" duplication on the
  // homepage while other pages still get the branded suffix.
  const fullTitle = title
    ? (/(^|\s)Viyora(\s|$)/i.test(title) ? title : `${title} · Viyora`)
    : DEFAULT_TITLE;
  const canonical = `${SITE_URL}${path}`;
  const ogImageUrl = ogImage?.startsWith("http") ? ogImage : `${SITE_URL}${ogImage}`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonical} />

      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large" />
      )}

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Viyora" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="en_IN" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImageUrl} />

      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
}
